/**
 * Extra Classes Service - API client for teacher-led paid tutoring
 * sessions: class setup, enrollment, assignments, and billing
 */
import api from '../utils/api';

const extraClassesService = {
  listAll: async () => {
    try {
      return await api.get('/api/extra-classes');
    } catch (error) {
      console.error('Error fetching extra classes:', error);
      throw error;
    }
  },

  createClass: async (data) => {
    try {
      return await api.post('/api/extra-classes', data);
    } catch (error) {
      console.error('Error creating extra class:', error);
      throw error;
    }
  },

  getTeacherClasses: async () => {
    try {
      return await api.get('/api/extra-classes/teacher/me');
    } catch (error) {
      console.error('Error fetching teacher extra classes:', error);
      throw error;
    }
  },

  getStudentEnrollments: async () => {
    try {
      return await api.get('/api/extra-classes/student/me');
    } catch (error) {
      console.error('Error fetching student extra class enrollments:', error);
      throw error;
    }
  },

  getParentEnrollments: async () => {
    try {
      return await api.get('/api/extra-classes/parent/me');
    } catch (error) {
      console.error('Error fetching parent extra class enrollments:', error);
      throw error;
    }
  },

  listAssignments: async (extraClassId) => {
    try {
      return await api.get(`/api/extra-classes/${extraClassId}/assignments`);
    } catch (error) {
      console.error('Error fetching extra class assignments:', error);
      throw error;
    }
  },

  createAssignment: async (extraClassId, data) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/assignments`, data);
    } catch (error) {
      console.error('Error creating extra class assignment:', error);
      throw error;
    }
  },

  getAssignment: async (extraClassId, assignmentId) => {
    try {
      return await api.get(`/api/extra-classes/${extraClassId}/assignments/${assignmentId}`);
    } catch (error) {
      console.error('Error fetching extra class assignment:', error);
      throw error;
    }
  },

  updateAssignment: async (extraClassId, assignmentId, data) => {
    try {
      return await api.put(`/api/extra-classes/${extraClassId}/assignments/${assignmentId}`, data);
    } catch (error) {
      console.error('Error updating extra class assignment:', error);
      throw error;
    }
  },

  deleteAssignment: async (extraClassId, assignmentId) => {
    try {
      return await api.delete(`/api/extra-classes/${extraClassId}/assignments/${assignmentId}`);
    } catch (error) {
      console.error('Error deleting extra class assignment:', error);
      throw error;
    }
  },

  submitAssignment: async (extraClassId, assignmentId, data) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/assignments/${assignmentId}/submit`, data);
    } catch (error) {
      console.error('Error submitting extra class assignment:', error);
      throw error;
    }
  },

  getSubmissions: async (extraClassId, assignmentId) => {
    try {
      return await api.get(`/api/extra-classes/${extraClassId}/assignments/${assignmentId}/submissions`);
    } catch (error) {
      console.error('Error fetching extra class submissions:', error);
      throw error;
    }
  },

  listSessions: async (extraClassId) => {
    try {
      return await api.get(`/api/extra-classes/${extraClassId}/sessions`);
    } catch (error) {
      console.error('Error fetching extra class sessions:', error);
      throw error;
    }
  },

  createSession: async (extraClassId, data) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/sessions`, data);
    } catch (error) {
      console.error('Error logging extra class session:', error);
      throw error;
    }
  },

  listEnrollments: async (extraClassId) => {
    try {
      return await api.get(`/api/extra-classes/${extraClassId}/enrollments`);
    } catch (error) {
      console.error('Error fetching extra class enrollments:', error);
      throw error;
    }
  },

  actOnEnrollment: async (extraClassId, enrollmentId, action) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/enrollments/${enrollmentId}/${action}`);
    } catch (error) {
      console.error('Error updating extra class enrollment:', error);
      throw error;
    }
  },

  // A parent pulling their child out of a class — stops future billing
  // cycles and frees the capacity slot for whoever's next on the waitlist.
  withdrawEnrollment: async (extraClassId, enrollmentId) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/enrollments/${enrollmentId}/withdraw`);
    } catch (error) {
      console.error('Error withdrawing extra class enrollment:', error);
      throw error;
    }
  },

  enroll: async (extraClassId, studentId) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/enroll`, { student_id: studentId });
    } catch (error) {
      console.error('Error registering for extra class:', error);
      throw error;
    }
  },

  // Read-only — no payout request/approval step exists anymore. Each paid
  // billing cycle already settled straight to the teacher's own bank/MoMo
  // account via their Paystack subaccount at payment time.
  getClassEarnings: async (extraClassId) => {
    try {
      return await api.get(`/api/extra-classes/${extraClassId}/earnings`);
    } catch (error) {
      console.error('Error fetching class earnings:', error);
      throw error;
    }
  },

  sendParentReminder: async (extraClassId, message) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/remind-parent`, { message });
    } catch (error) {
      console.error('Error sending extra class reminder:', error);
      throw error;
    }
  },

  remindSession: async (extraClassId, message) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/remind-session`, { message });
    } catch (error) {
      console.error('Error sending extra class session reminder:', error);
      throw error;
    }
  },

  // Initiates a real Paystack payment for a billing cycle — returns a
  // payment_url to redirect the parent to. The cycle is only marked paid
  // once the webhook verifies the charge, not by this call directly.
  payBillingCycle: async (extraClassId, billingCycleId) => {
    try {
      return await api.post(`/api/extra-classes/${extraClassId}/billing-cycles/${billingCycleId}/pay`, {});
    } catch (error) {
      console.error('Error initiating extra class payment:', error);
      throw error;
    }
  },

  // ---- School-admin oversight ----

  getAdminOverview: async () => {
    try {
      return await api.get('/api/extra-classes/admin/overview');
    } catch (error) {
      console.error('Error fetching extra classes admin overview:', error);
      throw error;
    }
  },

  updateClass: async (extraClassId, data) => {
    try {
      return await api.put(`/api/extra-classes/${extraClassId}`, data);
    } catch (error) {
      console.error('Error updating extra class:', error);
      throw error;
    }
  },
};

export default extraClassesService;
